// ---------------------------------------------------------------------------
// Progression config: the pacing sheet, as the client, server and sim read it
// ---------------------------------------------------------------------------
//
// DEFAULT_PROGRESSION is the sheet as imported into ./pacing.js (never edit
// that file; see importPacing.js). Like rules/config.js, a caller can run a
// variant: resolveProgression(override) deep-merges a partial override shaped
// like the data, e.g.
//
//   { inputs: { yieldToRawRate: 0.75 },
//     arenas: { 2: { killsToWin: 2 } },
//     structures: { Vault: { unlockArena: 2 } } }
//
// Unknown keys and wrong types are refused loudly (a typo in a variant file
// must not silently run defaults), and the result is re-checked as a whole.

import PACING from './pacing.js'
import { checkPacing } from './importPacing.js'

function deepFreeze(o) {
    if (o && typeof o === 'object' && !Object.isFrozen(o)) {
        Object.freeze(o)
        for (const v of Object.values(o)) deepFreeze(v)
    }
    return o
}

export const DEFAULT_PROGRESSION = deepFreeze(PACING)

// Merge `patch` into a copy of `base`. Keys must already exist; leaves must
// keep their type (null ↔ string is allowed, for optional names).
function merge(base, patch, at) {
    if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) {
        throw new Error(`Progression override ${at || '(root)'} must be an object`)
    }
    const out = Array.isArray(base) ? [...base] : { ...base }
    for (const [k, v] of Object.entries(patch)) {
        const where = at ? `${at}.${k}` : k
        if (!(k in base)) throw new Error(`Unknown progression config key "${where}"`)
        const cur = base[k]
        if (Array.isArray(cur)) {
            if (!Array.isArray(v)) throw new Error(`Progression config "${where}" must be an array`)
            out[k] = v
        } else if (cur !== null && typeof cur === 'object') {
            out[k] = merge(cur, v, where)
        } else {
            const nameLike = cur === null || typeof cur === 'string'
            const ok = nameLike ? v === null || typeof v === 'string' : typeof v === typeof cur
            if (!ok || (typeof v === 'number' && Number.isNaN(v))) throw new Error(`Progression config "${where}" must be a ${nameLike ? 'string or null' : typeof cur}`)
            out[k] = v
        }
    }
    return out
}

export function resolveProgression(override = null) {
    if (override == null) return DEFAULT_PROGRESSION
    return deepFreeze(checkPacing(merge(DEFAULT_PROGRESSION, override, '')))
}

// ── Lookups ─────────────────────────────────────────────────────────────────

export const arenaNumbers = (p = DEFAULT_PROGRESSION) => Object.keys(p.arenas).map(Number).sort((a, b) => a - b)
export const topArena = (p = DEFAULT_PROGRESSION) => Math.max(...arenaNumbers(p))
export const structureNames = (p = DEFAULT_PROGRESSION) => Object.keys(p.structures)

// Converters exist only while the switch is on: off, they never drop and no
// upgrade asks for refined resources.
export const isConverter = (name, p = DEFAULT_PROGRESSION) => !!p.structures[name]?.converterOutput

// Structure types a player can get by the given arena (Build card pool).
export function structuresUnlockedBy(arena, p = DEFAULT_PROGRESSION) {
    return structureNames(p).filter(n => p.structures[n].unlockArena <= arena && (p.inputs.convertersEnabled || !isConverter(n, p)))
}

// Lot types that have started dropping by the given arena (lot card pool).
export function lotTypesDroppedBy(arena, p = DEFAULT_PROGRESSION) {
    return Object.keys(p.lotTypes).filter(t => p.lotTypes[t].firstArena <= arena)
}
